import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SyncIcon from '@mui/icons-material/Sync';
import AssessmentIcon from '@mui/icons-material/Assessment';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import Collapse from '@mui/material/Collapse';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import {
  AnalyzerApiUrl,
  POLL_INTERVAL_MS,
  MAX_POLL_ATTEMPTS,
} from '../utilities/constants';
import ReportModal from './ReportModal';
import './JobsTable.css';

// ── Status helpers ─────────────────────────────────────────────────────────────

const STATUS_META = {
  uploading: { label: 'Uploading', color: 'default', Icon: HourglassEmptyIcon },
  inQueue: { label: 'In Queue', color: 'info', Icon: HourglassEmptyIcon },
  processing: { label: 'Processing', color: 'warning', Icon: SyncIcon },
  complete: { label: 'Complete', color: 'success', Icon: CheckCircleIcon },
  failed: { label: 'Failed', color: 'error', Icon: ErrorIcon },
};

function StatusChip({ status, error }) {
  const meta = STATUS_META[status] || STATUS_META.processing;
  const { Icon } = meta;
  const chip = (
    <Chip
      icon={
        status === 'processing' ? (
          <CircularProgress size={12} thickness={5} sx={{ color: 'inherit !important' }} />
        ) : (
          <Icon sx={{ fontSize: '14px !important' }} />
        )
      }
      label={meta.label}
      color={meta.color}
      size="small"
      sx={{ fontWeight: 600, cursor: status === 'failed' && error ? 'pointer' : 'default' }}
    />
  );

  if (status === 'failed' && error) {
    return (
      <Tooltip
        title={error}
        arrow
        placement="top"
        slotProps={{
          tooltip: {
            sx: { maxWidth: 350, fontSize: '0.75rem' },
          },
        }}
      >
        {chip}
      </Tooltip>
    );
  }

  return chip;
}

function Elapsed({ submittedAt, stopped }) {
  const [elapsed, setElapsed] = useState('');

  useEffect(() => {
    if (stopped) { setElapsed(''); return; }
    const update = () => {
      const s = Math.floor((Date.now() - new Date(submittedAt).getTime()) / 1000);
      const m = Math.floor(s / 60);
      setElapsed(`${m}:${String(s % 60).padStart(2, '0')}`);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [submittedAt, stopped]);

  if (stopped || !elapsed) return null;
  return <span className="elapsed-badge">{elapsed}</span>;
}

// ── Remediation summary (fetched from reports.json) ───────────────────────────

function countPassFail(checks = []) {
  let passed = 0;
  let failed = 0;
  for (const c of checks) {
    if (c.passed) passed++;
    else failed++;
  }
  return { passed, failed, total: passed + failed };
}

function ComplianceBadge({ level }) {
  const pass = level === 'pass';
  const unknown = level !== 'pass' && level !== 'fail';
  const bg = unknown ? '#f1f5f9' : pass ? '#f0fdf4' : '#fef2f2';
  const fg = unknown ? '#64748b' : pass ? '#15803d' : '#b91c1c';
  const border = unknown ? '#cbd5e1' : pass ? '#4ade80' : '#f87171';
  const label = unknown ? 'UNKNOWN' : pass ? 'COMPLIANT' : 'NON-COMPLIANT';
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1.25,
        py: 0.25,
        borderRadius: 1.5,
        backgroundColor: bg,
        border: `1.5px solid ${border}`,
        fontWeight: 700,
        fontSize: '0.7rem',
        color: fg,
        textTransform: 'uppercase',
      }}
    >
      {label}
    </Box>
  );
}

function StatTile({ label, value, sublabel }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.25,
        borderRadius: 1.5,
        minWidth: 120,
        flex: '1 1 120px',
        textAlign: 'center',
        borderColor: '#e2e8f0',
        backgroundColor: '#fff',
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 800, color: '#5F5BCD', lineHeight: 1.1 }}>
        {value}
      </Typography>
      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}>
        {label}
      </Typography>
      {sublabel && (
        <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.65rem' }}>
          {sublabel}
        </Typography>
      )}
    </Paper>
  );
}

function RemediationSummary({ job, idToken, open }) {
  const [state, setState] = useState({ loading: false, error: null, data: null });

  useEffect(() => {
    if (!open) return;
    if (!job?.requestId || !idToken) return;
    if (job.status !== 'complete') return;

    const requestId = job.requestId;
    const cachedReportUrl = job.reportUrl;
    let cancelled = false;

    (async () => {
      setState({ loading: true, error: null, data: null });
      try {
        let reportUrl = cachedReportUrl;
        if (!reportUrl) {
          const resp = await fetch(`${AnalyzerApiUrl}/status/${requestId}`, {
            headers: { Authorization: idToken },
          });
          if (!resp.ok) throw new Error(`Status endpoint returned ${resp.status}`);
          const data = await resp.json();
          reportUrl = data.report_url;
        }
        if (!reportUrl) throw new Error('No report available for this job.');

        const reportResp = await fetch(reportUrl);
        if (!reportResp.ok) throw new Error(`Failed to fetch report (${reportResp.status})`);
        const json = await reportResp.json();
        if (!cancelled) setState({ loading: false, error: null, data: json });
      } catch (err) {
        if (!cancelled) setState({ loading: false, error: err.message, data: null });
      }
    })();

    return () => { cancelled = true; };
  }, [open, job?.requestId, job?.status, job?.reportUrl, idToken]);

  if (job.status === 'inQueue' || job.status === 'processing' || job.status === 'uploading') {
    return (
      <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic' }}>
        Remediation summary will be available when processing completes.
      </Typography>
    );
  }

  if (job.status === 'failed') {
    return (
      <Alert severity="error" sx={{ borderRadius: 2 }}>
        {job.error || 'This job failed before a remediation report could be generated.'}
      </Alert>
    );
  }

  if (state.loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
        <CircularProgress size={18} sx={{ color: '#5F5BCD' }} aria-label="Loading summary" />
        <Typography variant="body2" sx={{ color: '#64748b' }}>Loading remediation summary…</Typography>
      </Box>
    );
  }

  if (state.error) {
    return <Alert severity="warning" sx={{ borderRadius: 2 }}>{state.error}</Alert>;
  }

  if (!state.data) return null;

  const preLevel = state.data.pre_remediation?.compliance_level;
  const postLevel = state.data.post_remediation?.compliance_level;
  const pre = countPassFail(state.data.pre_remediation?.checks);
  const post = countPassFail(state.data.post_remediation?.checks);
  const summary = state.data.summary || {};
  const checksFixed = Math.max(0, post.passed - pre.passed);

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f0e2e', mb: 1 }}>
        Remediation Summary
      </Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 2,
          p: 1.5,
          borderRadius: 2,
          backgroundColor: '#f8fafc',
          border: '1px solid #e2e8f0',
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mb: 0.5 }}>
            BEFORE
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ComplianceBadge level={preLevel} />
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              {pre.passed}/{pre.total} checks passed
            </Typography>
          </Box>
        </Box>

        <Box sx={{ fontSize: 22, color: '#94a3b8', fontWeight: 700 }}>→</Box>

        <Box>
          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mb: 0.5 }}>
            AFTER
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ComplianceBadge level={postLevel} />
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              {post.passed}/{post.total} checks passed
            </Typography>
          </Box>
        </Box>

        {checksFixed > 0 && (
          <>
            <Divider orientation="vertical" flexItem />
            <Box>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mb: 0.5 }}>
                IMPROVEMENT
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#16a34a' }}>
                +{checksFixed} check{checksFixed === 1 ? '' : 's'} now passing
              </Typography>
            </Box>
          </>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <StatTile label="Pages Processed" value={summary.pages_processed ?? '—'} />
        <StatTile label="Elements Tagged" value={summary.total_elements_tagged ?? '—'} />
        <StatTile
          label="Figures w/ Alt Text"
          value={summary.total_figures_with_alt ?? '—'}
          sublabel={
            summary.total_figures_without_alt != null
              ? `${summary.total_figures_without_alt} missing`
              : null
          }
        />
        <StatTile label="Invisible Text Overlays" value={summary.invisible_text_overlays_added ?? '—'} />
        <StatTile
          label="Warnings / Errors"
          value={`${summary.warnings?.length ?? 0} / ${summary.errors?.length ?? 0}`}
        />
      </Box>
    </Box>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function JobsTable({ jobs, onJobsUpdate, idToken, loading, onRefresh }) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuJob, setMenuJob] = useState(null);
  const [downloading, setDownloading] = useState(new Set());
  const [reportModalJob, setReportModalJob] = useState(null);
  const [expandedJobIds, setExpandedJobIds] = useState(new Set());
  const pollTimers = useRef({});

  const toggleExpanded = (requestId) => {
    setExpandedJobIds((prev) => {
      const next = new Set(prev);
      if (next.has(requestId)) next.delete(requestId);
      else next.add(requestId);
      return next;
    });
  };

  // ── Poll a single job for completion ────────────────────────────────────────
  const pollJob = useCallback(
    (job) => {
      let attempts = 0;

      const timer = setInterval(async () => {
        attempts++;
        if (attempts > MAX_POLL_ATTEMPTS) {
          clearInterval(timer);
          onJobsUpdate((prev) =>
            prev.map((j) =>
              j.requestId === job.requestId ? { ...j, status: 'failed' } : j
            )
          );
          return;
        }

        try {
          const resp = await fetch(`${AnalyzerApiUrl}/status/${job.requestId}`, {
            headers: { Authorization: idToken },
          });

          if (!resp.ok) return; // transient error — keep polling

          const data = await resp.json();

          if (data.status === 'COMPLETED') {
            clearInterval(timer);
            onJobsUpdate((prev) =>
              prev.map((j) =>
                j.requestId === job.requestId
                  ? {
                    ...j,
                    status: 'complete',
                    completedAt: new Date().toISOString(),
                    downloadUrl: data.url,
                    reportUrl: data.report_url,
                    pageCount: data.page_count ?? j.pageCount,
                    durationSeconds: data.duration_seconds ?? j.durationSeconds,
                  }
                  : j
              )
            );
          } else if (data.status === 'FAILED') {
            clearInterval(timer);
            onJobsUpdate((prev) =>
              prev.map((j) =>
                j.requestId === job.requestId
                  ? { ...j, status: 'failed', error: data.error }
                  : j
              )
            );
          } else if (data.status === 'PROCESSING') {
            onJobsUpdate((prev) =>
              prev.map((j) =>
                j.requestId === job.requestId ? { ...j, status: 'processing' } : j
              )
            );
          }
          // PENDING / PROCESSING — keep polling
        } catch (err) {
          console.warn('Poll error', err);
        }
      }, POLL_INTERVAL_MS);

      pollTimers.current[job.requestId] = timer;
    },
    [idToken, onJobsUpdate]
  );

  // Start polling for every job in 'processing' or 'inQueue' status
  useEffect(() => {
    jobs.forEach((job) => {
      if ((job.status === 'processing' || job.status === 'inQueue') && job.requestId && !pollTimers.current[job.requestId]) {
        pollJob(job);
      }
    });
    // Cleanup timers for completed/failed/removed jobs
    return () => {
      Object.entries(pollTimers.current).forEach(([id, timer]) => {
        const job = jobs.find((j) => j.requestId === id);
        if (!job || (job.status !== 'processing' && job.status !== 'inQueue')) {
          clearInterval(timer);
          delete pollTimers.current[id];
        }
      });
    };
  }, [jobs, pollJob]);

  // ── Download ────────────────────────────────────────────────────────────────
  const handleDownload = async (job, type = 'pdf') => {
    const key = `${job.requestId}-${type}`;
    setDownloading((prev) => new Set(prev).add(key));

    try {
      let url = type === 'report' ? job.reportUrl : job.downloadUrl;

      // If URL isn't cached on the job, fetch it from the status endpoint
      if (!url && job.requestId && idToken) {
        const resp = await fetch(`${AnalyzerApiUrl}/status/${job.requestId}`, {
          headers: { Authorization: idToken },
        });
        if (resp.ok) {
          const data = await resp.json();
          url = type === 'report' ? data.report_url : data.url;

          // Cache the URLs on the job so subsequent clicks don't re-fetch
          if (data.url || data.report_url) {
            onJobsUpdate((prev) =>
              prev.map((j) =>
                j.requestId === job.requestId
                  ? {
                    ...j,
                    downloadUrl: data.url || j.downloadUrl,
                    reportUrl: data.report_url || j.reportUrl,
                  }
                  : j
              )
            );
          }
        }
      }

      if (!url) return;

      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloading((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const openMenu = (e, job) => { setMenuAnchor(e.currentTarget); setMenuJob(job); };
  const closeMenu = () => { setMenuAnchor(null); setMenuJob(null); };

  if (jobs.length === 0) {
    if (loading) {
      return (
        <Box className="jobs-empty">
          <CircularProgress aria-label="Loading jobs" />
          <Typography variant="body2" sx={{ mt: 2, color: '#475569' }}>
            Loading jobs...
          </Typography>
        </Box>
      );
    }
    return (
      <Box className="jobs-empty">
        <HourglassEmptyIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 2 }} aria-hidden="true" />
        <Typography variant="h6" sx={{ color: '#475569', fontWeight: 600 }}>
          No jobs yet
        </Typography>
        <Typography variant="body2" sx={{ color: '#475569' }}>
          Upload a PDF to start your first remediation job.
        </Typography>
      </Box>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography component="h1" variant="h5" sx={{ fontWeight: 700, color: '#0f0e2e', flexGrow: 1 }}>
          My Jobs
        </Typography>
        <Tooltip title="Refresh">
          <IconButton
            onClick={onRefresh}
            aria-label="Refresh jobs"
            size="small"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} aria-label="Refreshing" /> : <RefreshIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table aria-label="Remediation jobs">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 700, width: 40 }} padding="checkbox" aria-label="Expand" />
              <TableCell sx={{ fontWeight: 700 }}>File Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Pages</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Language</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Submitted</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Duration</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[...jobs].reverse().map((job) => {
              const isProcessing = job.status === 'processing' || job.status === 'inQueue';
              const isComplete = job.status === 'complete';
              const downloadingPdf = downloading.has(`${job.requestId}-pdf`);
              const expanded = expandedJobIds.has(job.requestId);


              let duration = '';
              const fmtSeconds = (s) => s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
              if (job.durationSeconds != null) {
                duration = fmtSeconds(job.durationSeconds);
              } else if (job.completedAt && job.submittedAt) {
                const s = Math.floor(
                  (new Date(job.completedAt) - new Date(job.submittedAt)) / 1000
                );
                duration = fmtSeconds(s);
              }

              return (
                <React.Fragment key={job.requestId}>
                <TableRow
                  hover
                  onClick={() => toggleExpanded(job.requestId)}
                  aria-expanded={expanded}
                  sx={{
                    cursor: 'pointer',
                    '& > td': { borderBottom: expanded ? 'none' : undefined },
                  }}
                >
                  <TableCell padding="checkbox">
                    <IconButton
                      size="small"
                      aria-label={expanded ? 'Collapse summary' : 'Expand summary'}
                      onClick={(e) => { e.stopPropagation(); toggleExpanded(job.requestId); }}
                    >
                      {expanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          maxWidth: 220,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: '#0f0e2e',
                        }}
                        title={job.fileName}
                      >
                        {job.fileName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#475569' }}>
                        {job.title !== job.fileName ? job.title : ''}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <StatusChip status={job.status} error={job.error} />
                      {isProcessing && (
                        <LinearProgress
                          sx={{
                            width: 80,
                            borderRadius: 1,
                            mt: 0.5,
                            '& .MuiLinearProgress-bar': { backgroundColor: '#5F5BCD' },
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">
                      {job.pagesProcessed != null
                        ? job.pagesProcessed
                        : job.pageCount != null
                          ? `${job.pageCount}`
                          : '—'}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">{job.lang ?? '—'}</Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">
                      {new Date(job.submittedAt).toLocaleString()}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    {isProcessing ? (
                      <Elapsed submittedAt={job.submittedAt} stopped={!isProcessing} />
                    ) : (
                      <Typography variant="body2" sx={{ color: '#475569' }}>
                        {duration || '—'}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    {isComplete ? (
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Tooltip title="Download remediated PDF">
                          <span>
                            <Button
                              size="small"
                              variant="contained"
                              aria-label={`Download remediated PDF for ${job.fileName}`}
                              startIcon={
                                downloadingPdf ? (
                                  <CircularProgress size={12} sx={{ color: 'inherit' }} aria-label="Downloading" />
                                ) : (
                                  <DownloadIcon />
                                )
                              }
                              disabled={downloadingPdf}
                              onClick={() => handleDownload(job, 'pdf')}
                              sx={{
                                backgroundColor: '#5F5BCD',
                                fontSize: '0.75rem',
                                '&:hover': { backgroundColor: '#4e4ab5' },
                              }}
                            >
                              PDF
                            </Button>
                          </span>
                        </Tooltip>

                        <Tooltip title="View accessibility report">
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              aria-label={`View accessibility report for ${job.fileName}`}
                              startIcon={<AssessmentIcon />}
                              onClick={() => setReportModalJob(job)}
                              sx={{
                                borderColor: '#5F5BCD',
                                color: '#5F5BCD',
                                fontSize: '0.75rem',
                                '&:hover': { borderColor: '#4e4ab5', backgroundColor: 'rgba(95,91,205,0.06)' },
                              }}
                            >
                              Report
                            </Button>
                          </span>
                        </Tooltip>

                      </Box>
                    ) : (
                      <IconButton
                        size="small"
                        onClick={(e) => openMenu(e, job)}
                        aria-label={`Actions for ${job.fileName}`}
                        disabled={job.status === 'uploading'}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>

                <TableRow sx={{ '& > td': { borderBottom: expanded ? '1px solid #e2e8f0' : 'none', py: 0 } }}>
                  <TableCell colSpan={8} sx={{ p: 0, backgroundColor: expanded ? '#fafbff' : 'transparent' }}>
                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                      <Box sx={{ px: 3, py: 2, overflowX: 'auto', maxWidth: '100%' }}>
                        <RemediationSummary job={job} idToken={idToken} open={expanded} />
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Context menu for non-complete jobs */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem
          disabled
          sx={{ fontSize: '0.8rem', color: '#475569', opacity: '1 !important' }}
        >
          ID: {menuJob?.requestId?.slice(0, 20)}…
        </MenuItem>
      </Menu>

      {/* Accessibility report modal */}
      <ReportModal
        open={Boolean(reportModalJob)}
        onClose={() => setReportModalJob(null)}
        job={reportModalJob}
        idToken={idToken}
      />
    </motion.div>
  );
}
